/**
 * React Query Client Configuration
 * 
 * Centralized configuration for TanStack React Query.
 * Provides sensible defaults for caching, retries, and stale time.
 * 
 * @module lib/queryClient
 */

import { QueryClient } from '@tanstack/react-query';
import logger from '@/utils/logger';

/**
 * Default query options optimized for production reliability
 */
const defaultQueryOptions = {
  queries: {
    // Data is considered fresh for 5 minutes
    staleTime: 5 * 60 * 1000,
    
    // Cache data for 30 minutes
    gcTime: 30 * 60 * 1000,
    
    // Retry 3 times with exponential backoff (1s → 2s → 4s)
    retry: 3,
    retryDelay: (attemptIndex) => Math.min(1000 * Math.pow(2, attemptIndex), 8000),
    
    // Don't refetch on window focus (can be noisy)
    refetchOnWindowFocus: false,
    
    // Refetch when reconnecting to network
    refetchOnReconnect: true,
    
    // Keep previous data while fetching new data
    placeholderData: (previousData) => previousData,
  },
  mutations: {
    // Don't retry mutations (not idempotent)
    retry: 0,
    
    // Log mutation errors
    onError: (error) => {
      logger.error('Mutation error:', error);
    },
  },
};

/**
 * Create and configure the QueryClient instance
 */
export const queryClient = new QueryClient({
  defaultOptions: defaultQueryOptions,
});

/**
 * Query key factory for consistent cache key generation
 *
 * @example
 * queryKeys.clients.list({ category: 'VIP' })
 * queryKeys.clients.detail('cli-123')
 */
export const queryKeys = {
  // Client queries
  clients: {
    all: ['clients'],
    lists: () => [...queryKeys.clients.all, 'list'],
    list: (filters) => [...queryKeys.clients.lists(), filters],
    detail: (id) => [...queryKeys.clients.all, 'detail', id],
  },
};

export default queryClient;
